import { useState, useEffect, useCallback } from 'react';
import { Menu, Bell, LogOut, AlertTriangle, AlertCircle, Info, RefreshCw, ChevronsUpDown, Check } from '@/utils/icons';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { useUIStore } from '@/stores/uiStore';
import { useDashboardStore } from '@/stores/useDashboardStore';
import { logout, getAlerts, triggerPoll, triggerDemoPerfect, getAdminOrganizations, setDevTenantId } from '@/services/api';
import { cn } from '@/lib/utils';
import { formatTimeAgo } from '@/utils/formatters';
import type { Alert } from '@/types/metrics';
import type { Organization } from '@/types/tenant';

const severityIcons: Record<string, typeof AlertTriangle> = {
  critical: AlertTriangle,
  warning: AlertCircle,
  info: Info,
};

const getAlertIcon = (alert: Alert) => {
  if (alert.type === 'flapping') return RefreshCw;
  return severityIcons[alert.severity] || Info;
};

const severityClasses: Record<string, string> = {
  critical: 'dls-bg-status-offline-quarternary dls-border-status-offline-tertiary dls-text-status-offline',
  warning: 'dls-bg-status-warning-quarternary dls-border-status-warning-tertiary dls-text-status-warning',
  info: 'dls-bg-status-info/10 dls-border-status-info/30 dls-text-status-info',
};

type ClassFilter = 'all' | 'customer' | 'internal' | 'demo';

const CLASS_FILTER_LABELS: Record<ClassFilter, string> = {
  all: 'All',
  customer: 'Customers',
  internal: 'Internal',
  demo: 'Demo',
};

function OrgSwitcher() {
  const { session, selectedOrgId, setSelectedOrgId, selectedPlatformId } = useUIStore();
  const triggerRefresh = useDashboardStore((s) => s.triggerRefresh);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [classFilter, setClassFilter] = useState<ClassFilter>('all');

  useEffect(() => {
    if (session?.role !== 'dev') return;
    getAdminOrganizations(selectedPlatformId ?? undefined)
      .then((data) => {
        const active = data.organizations.filter((o) => o.status === 'active');
        setOrgs(active);
        // Reset org selection if current org isn't in the new platform's list.
        // Read selectedOrgId via getState() so the effect re-runs ONLY on
        // platform/role changes, not when the user picks a different org.
        const currentOrgId = useUIStore.getState().selectedOrgId;
        if (currentOrgId && !active.some((o) => o.id === currentOrgId)) {
          setSelectedOrgId(null);
          setDevTenantId(null);
          triggerRefresh();
        }
      })
      .catch(() => {});
  }, [session?.role, selectedPlatformId, setSelectedOrgId, triggerRefresh]);

  const handleSelect = (org: Organization | null) => {
    if (org) {
      setSelectedOrgId(org.id);
      setDevTenantId(org.id);
    } else {
      setSelectedOrgId(null);
      setDevTenantId(null);
    }
    setOpen(false);
    setSearch('');
    // Re-fetch all data with new tenant context
    triggerRefresh();
  };

  const filtered = orgs.filter((o) => {
    if (classFilter !== 'all' && o.classification !== classFilter) return false;
    if (search && !o.name.toLowerCase().includes(search.toLowerCase()) &&
      !(o.domainName?.toLowerCase().includes(search.toLowerCase()) ?? false)) return false;
    return true;
  });

  const selectedOrg = orgs.find((o) => o.id === selectedOrgId);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="secondary" size="sm" className="dls-gap-1 dls-max-w-[220px] dls-truncate">
          <span className="dls-truncate dls-text-xs">
            {selectedOrg
              ? selectedOrg.name
              : classFilter === 'all'
                ? 'All Organizations'
                : `All ${CLASS_FILTER_LABELS[classFilter]}`}
          </span>
          <ChevronsUpDown className="dls-h-3 dls-w-3 dls-shrink-0 dls-opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="dls-w-[300px] dls-p-0" align="end">
        <div className="dls-p-2 dls-space-y-2">
          <Input
            placeholder="Search organizations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="dls-h-8 dls-text-sm"
          />
          <div className="dls-flex dls-gap-1">
            {(Object.keys(CLASS_FILTER_LABELS) as ClassFilter[]).map((key) => (
              <button
                key={key}
                onClick={() => setClassFilter(key)}
                className={cn(
                  'dls-flex-1 dls-text-[11px] dls-py-1 dls-rounded-md dls-transition-colors',
                  classFilter === key
                    ? 'dls-bg-neeve-primary dls-text-white'
                    : 'dls-bg-muted dls-text-muted-foreground hover:dls-bg-accent'
                )}
              >
                {CLASS_FILTER_LABELS[key]}
              </button>
            ))}
          </div>
        </div>
        <div className="dls-max-h-[300px] dls-overflow-y-auto dls-px-1 dls-pb-1">
          <button
            className={cn(
              'dls-w-full dls-flex dls-items-center dls-gap-2 dls-px-2 dls-py-1.5 dls-rounded-sm dls-text-sm hover:dls-bg-accent dls-cursor-pointer',
              !selectedOrgId && 'dls-bg-accent'
            )}
            onClick={() => handleSelect(null)}
          >
            <Check className={cn('dls-h-3 dls-w-3', selectedOrgId && 'dls-invisible')} />
            <span className="dls-font-medium">
              {classFilter === 'all' ? 'All Organizations' : `All ${CLASS_FILTER_LABELS[classFilter]}`}
            </span>
            <span className="dls-text-xs dls-text-muted-foreground dls-ml-auto">{filtered.length}</span>
          </button>
          {filtered.map((org) => (
            <button
              key={org.id}
              className={cn(
                'dls-w-full dls-flex dls-items-center dls-gap-2 dls-px-2 dls-py-1.5 dls-rounded-sm dls-text-sm hover:dls-bg-accent dls-cursor-pointer',
                selectedOrgId === org.id && 'dls-bg-accent'
              )}
              onClick={() => handleSelect(org)}
            >
              <Check className={cn('dls-h-3 dls-w-3', selectedOrgId !== org.id && 'dls-invisible')} />
              <div className="dls-flex-1 dls-text-left dls-min-w-0">
                <span className="dls-block dls-truncate">{org.name}</span>
                {org.domainName && (
                  <span className="dls-block dls-text-xs dls-text-muted-foreground dls-truncate">
                    {org.domainName}
                  </span>
                )}
              </div>
              <span className={cn(
                'dls-text-[10px] dls-px-1.5 dls-py-0.5 dls-rounded-full dls-shrink-0',
                org.classification === 'customer' && 'dls-bg-neeve-tertiary/20 dls-text-neeve-primary',
                org.classification === 'internal' && 'dls-bg-muted dls-text-muted-foreground',
                org.classification === 'demo' && 'dls-bg-status-warning/10 dls-text-status-warning',
              )}>
                {org.classification}
              </span>
            </button>
          ))}
          {filtered.length === 0 && (
            <p className="dls-text-xs dls-text-muted-foreground dls-text-center dls-py-4">No organizations found</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function Header() {
  const { session, setSidebarOpen, sidebarOpen, setSession } = useUIStore();
  const triggerRefresh = useDashboardStore((s) => s.triggerRefresh);
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [demoPolling, setDemoPolling] = useState(false);

  const fetchAlerts = useCallback(async () => {
    try {
      const data = await getAlerts();
      setAlerts(data.alerts);
    } catch {
      // Silently fail — alerts are non-critical
    }
  }, []);

  useEffect(() => {
    if (session?.authenticated) {
      fetchAlerts();
      const interval = setInterval(fetchAlerts, 60_000);
      return () => clearInterval(interval);
    }
  }, [session?.authenticated, fetchAlerts]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      // Server-side session cleanup may fail, but we still clear client state
    }
    setSession({ authenticated: false, tenant: null });
    navigate('/login');
  };

  const handleAlertClick = (nodeId: string) => {
    setDrawerOpen(false);
    navigate(`/nodes/${nodeId}`);
  };

  const criticalCount = alerts.filter((a) => a.severity === 'critical').length;
  const isDev = session?.role === 'dev';

  return (
    <header className="dls-sticky dls-top-0 dls-z-40 dls-border-b dls-border-border dls-bg-background/95 dls-backdrop-blur supports-[backdrop-filter]:dls-bg-background/60">
      <div className="dls-flex dls-h-16 dls-items-center dls-px-4 dls-gap-4">
        <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:dls-hidden" aria-label="Toggle sidebar">
          <Menu className="dls-h-5 dls-w-5" />
        </Button>

        <div className="dls-ml-auto dls-flex dls-items-center dls-gap-2">
          {/* Org Switcher for dev users */}
          {isDev && <OrgSwitcher />}

          {session?.tenant && !isDev &&
            (session?.isDemo ? (
              <button
                onClick={async () => {
                  if (demoPolling) return;
                  setDemoPolling(true);
                  try {
                    await triggerDemoPerfect();
                    triggerRefresh();
                    fetchAlerts();
                  } catch {
                    // Silently fail — non-critical
                  } finally {
                    setTimeout(() => setDemoPolling(false), 600);
                  }
                }}
                className="dls-text-sm dls-text-muted-foreground dls-hidden md:dls-inline dls-cursor-default"
              >
                {session.tenant.name}
              </button>
            ) : (
              <span className="dls-text-sm dls-text-muted-foreground dls-hidden md:dls-inline">
                {session.tenant.name}
              </span>
            ))}
          {session?.isNeeveEmployee && (
            <span className="dls-inline-flex dls-items-center dls-rounded-full dls-bg-neeve-tertiary/20 dls-px-2.5 dls-py-0.5 dls-text-xs dls-font-semibold dls-text-neeve-primary dls-border dls-border-neeve-tertiary/30">
              DEV
            </span>
          )}
          {session?.isDemo && (
            <button
              onClick={async () => {
                if (demoPolling) return;
                setDemoPolling(true);
                try {
                  await triggerPoll();
                  triggerRefresh();
                  fetchAlerts();
                } catch {
                  // Silently fail — non-critical
                } finally {
                  setTimeout(() => setDemoPolling(false), 600);
                }
              }}
              className="dls-inline-flex dls-items-center dls-gap-1 dls-rounded-full dls-bg-status-warning-quarternary dls-px-2.5 dls-py-0.5 dls-text-xs dls-font-semibold dls-text-status-warning dls-border dls-border-status-warning-tertiary hover:dls-bg-status-warning-tertiary dls-transition-colors dls-cursor-pointer"
              title="Click to simulate a poll cycle"
            >
              <RefreshCw className={cn('dls-h-3 dls-w-3', demoPolling && 'dls-animate-spin')} />
              DEMO
            </button>
          )}

          <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="dls-relative" aria-label="View alerts">
                <Bell className="dls-h-4 dls-w-4" />
                {criticalCount > 0 && (
                  <span className="dls-absolute -dls-top-0.5 -dls-right-0.5 dls-flex dls-h-4 dls-w-4 dls-items-center dls-justify-center dls-rounded-full dls-bg-status-offline dls-text-[10px] dls-font-bold dls-text-white">
                    {criticalCount > 9 ? '9+' : criticalCount}
                  </span>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent className="dls-flex dls-flex-col dls-p-0">
              <SheetHeader className="dls-border-b dls-px-6 dls-pr-12 dls-py-4">
                <SheetTitle className="dls-flex dls-items-center dls-gap-2">
                  <Bell className="dls-h-5 dls-w-5" />
                  Alerts
                  {alerts.length > 0 && (
                    <span className="dls-ml-auto dls-text-sm dls-font-normal dls-text-muted-foreground">
                      {alerts.length} Active
                    </span>
                  )}
                </SheetTitle>
                <SheetDescription>Active issues across all nodes</SheetDescription>
              </SheetHeader>

              <div className="dls-flex-1 dls-overflow-y-auto dls-p-4">
                {alerts.length === 0 ? (
                  <p className="dls-text-sm dls-text-muted-foreground dls-text-center dls-py-12">
                    No active alerts
                  </p>
                ) : (
                  <div className="dls-space-y-2">
                    {alerts.map((alert, index) => {
                      const Icon = getAlertIcon(alert);
                      return (
                        <div
                          key={`${alert.nodeId}-${index}`}
                          className={cn(
                            'dls-flex dls-items-start dls-gap-3 dls-p-3 dls-rounded-lg dls-border dls-cursor-pointer dls-transition-colors hover:dls-opacity-80',
                            severityClasses[alert.severity]
                          )}
                          onClick={() => handleAlertClick(alert.nodeId)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleAlertClick(alert.nodeId);
                            }
                          }}
                        >
                          <Icon className="dls-h-4 dls-w-4 dls-mt-0.5 dls-flex-shrink-0" />
                          <div className="dls-flex-1 dls-min-w-0">
                            <span className="dls-font-medium dls-truncate dls-block" title={alert.nodeName}>{alert.nodeName}</span>
                            <p className="dls-text-sm dls-opacity-90">{alert.message}</p>
                            {alert.since && (
                              <p className="dls-text-xs dls-opacity-75 dls-mt-1">
                                Since {formatTimeAgo(alert.since)}
                              </p>
                            )}
                            {alert.lastContact && (
                              <p className="dls-text-xs dls-opacity-75 dls-mt-1">
                                Last contact {formatTimeAgo(alert.lastContact)}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>

          {session?.authenticated && (
            <Button variant="ghost" size="icon" onClick={handleLogout} aria-label="Logout">
              <LogOut className="dls-h-4 dls-w-4" />
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
